import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { articles } from '../data/articles';
import NavItem from './NavItem';
import NavDropdown from './NavDropdown';
import { getPageKeywords } from '../utils/seoUtils';
import type { Article } from '../types/article';

interface HeaderProps {
  activeLink?: 'home' | 'articles' | 'plugins' | 'convert' | 'faq';
  metadata?: Article;
}

const Header: React.FC<HeaderProps> = ({ activeLink, metadata }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Get current language from URL or i18n
  const currentLang = lang || i18n.language;
  
  // Get path without language prefix
  const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
  
  // Get current article if we're on an article page
  const currentArticle = articles.find(article => 
    currentPath.endsWith(`/articles/${article.slug}`)
  );

  // Get page metadata
  const getMetadata = () => {
    const baseTitle = 'ShiftPic - Free Image Converter';
    const baseDescription = t('app.description.default').substring(0, 160); // Limit to 160 characters for SEO
    const baseImage = 'https://shiftpic.com/og-image.jpg';
    
    // Construct canonical URL with language prefix
    const currentUrl = `https://shiftpic.com/${currentLang}${pathWithoutLang}`;
    
    // Get page-specific keywords
    const keywords = getPageKeywords(pathWithoutLang, currentArticle?.slug);
    
    // Format the locale properly for Open Graph (e.g., "en" -> "en_US")
    const localeMap: Record<string, string> = {
      'en': 'en_US',
      'es': 'es_ES',
      'de': 'de_DE',
      'it': 'it_IT',
      'cs': 'cs_CZ'
    };

    const currentLocale = currentLang.split('-')[0];
    const ogLocale = localeMap[currentLocale] || 'en_US';

    if (currentArticle && metadata) {
      // Make sure imageUrl is an absolute URL
      const imageUrl = metadata.imageUrl ? 
        (metadata.imageUrl.startsWith('http') ? metadata.imageUrl : `https://shiftpic.com${metadata.imageUrl}`) : 
        baseImage;
        
      return {
        title: `${metadata.title} - ShiftPic`,
        description: metadata.description,
        keywords: `${metadata.category}, ${keywords}, image conversion, ${metadata.title}`,
        image: imageUrl,
        url: currentUrl,
        type: 'article',
        locale: ogLocale,
        publishedTime: metadata.date,
        modifiedTime: metadata.date,
        author: 'Petr Novak',
        section: metadata.category,
        tags: `${metadata.category}, image conversion, image format`,
        schema: {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": metadata.title,
          "description": metadata.description,
          "image": imageUrl,
          "datePublished": metadata.date,
          "author": {
            "@type": "Person",
            "name": "Petr Novak",
            "url": "https://pnovak.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "ShiftPic",
            "logo": {
              "@type": "ImageObject",
              "url": "https://shiftpic.com/logo.svg"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": currentUrl
          }
        }
      };
    }

    // Default website schema
    return {
      title: baseTitle,
      description: baseDescription,
      keywords: keywords,
      image: baseImage,
      url: currentUrl,
      type: 'website',
      locale: ogLocale,
      schema: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "ShiftPic",
        "description": baseDescription,
        "url": "https://shiftpic.com",
        "applicationCategory": "ImageConverter",
        "operatingSystem": "Any",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "author": {
          "@type": "Person",
          "name": "Petr Novak",
          "url": "https://pnovak.com"
        }
      }
    };
  };

  const pageMetadata = getMetadata();

  // Effect to manage body scroll
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    // Cleanup function to ensure we remove the class when component unmounts
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (mobileMenuOpen && !target.closest('.mobile-menu') && !target.closest('.mobile-menu-button')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <header className="bg-gray-100" role="banner">
      <Helmet>
        {/* Basic meta tags */}
        <title>{pageMetadata.title}</title>
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={pageMetadata.description} />
        <meta name="keywords" content={pageMetadata.keywords} />
        <meta name="author" content="Petr Novak" />
        <meta name="theme-color" content="#260A2F" />

        {/* Canonical URL */}
        <link rel="canonical" href={pageMetadata.url} />
        <link rel="alternate" href="https://shiftpic.com/" hrefLang="x-default" />
        
        {/* Language variations */}
        <link rel="alternate" href={`https://shiftpic.com/en${pathWithoutLang}`} hrefLang="en" />
        <link rel="alternate" href={`https://shiftpic.com/es${pathWithoutLang}`} hrefLang="es" />
        <link rel="alternate" href={`https://shiftpic.com/de${pathWithoutLang}`} hrefLang="de" />
        <link rel="alternate" href={`https://shiftpic.com/it${pathWithoutLang}`} hrefLang="it" />
        <link rel="alternate" href={`https://shiftpic.com/cs${pathWithoutLang}`} hrefLang="cs" />
        
        {/* OpenGraph meta tags */}
        <meta property="og:site_name" content="ShiftPic" />
        <meta property="og:type" content={pageMetadata.type} />
        <meta property="og:title" content={pageMetadata.title} />
        <meta property="og:description" content={pageMetadata.description} />
        <meta property="og:image" content={pageMetadata.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageMetadata.url} />
        <meta property="og:locale" content={pageMetadata.locale} />
        
        {/* Twitter meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@stordverg" />
        <meta name="twitter:title" content={pageMetadata.title} />
        <meta name="twitter:description" content={pageMetadata.description} />
        <meta name="twitter:image" content={pageMetadata.image} />
        
        {/* Article specific meta tags */}
        {pageMetadata.type === 'article' && (
          <>
            <meta property="article:published_time" content={pageMetadata.publishedTime} />
            <meta property="article:modified_time" content={pageMetadata.modifiedTime} />
            <meta property="article:author" content={pageMetadata.author} />
            <meta property="article:section" content={pageMetadata.section} />
            <meta property="article:tag" content={pageMetadata.tags} />
          </>
        )}
        
        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify(pageMetadata.schema)}
        </script>
      </Helmet>
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link to={`/${lang || i18n.language.split('-')[0]}`}>
              <img src="/logo.svg" alt="ShiftPic Logo" width={144} height={40} className="h-10" />
            </Link>
            <svg xmlns="http://www.w3.org/2000/svg" width="5" height="22" fill="none" viewBox="0 0 5 22" className="hidden md:block mx-4" aria-hidden="true">
              <path fill="#9FE870" d="M4.76.989 1.436 21.011H0L3.324.989H4.76Z"/>
            </svg>
            <p className="text-primary font-medium hidden md:inline mb-0"><i>{t('app.header')}</i></p>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <NavItem to="/" active={activeLink === 'home'}>
              {t('nav.converter')}
            </NavItem>
            <NavDropdown active={activeLink === 'convert'} />
            <NavItem to="/articles" active={activeLink === 'articles'}>
              {t('nav.articles')}
            </NavItem>
            <NavItem to="/plugins" active={activeLink === 'plugins'}>
              {t('nav.plugins')}
            </NavItem>
            <NavItem to="/faq" active={activeLink === 'faq'}>
              {t('nav.faq')}
            </NavItem>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-600 hover:text-primary transition-colors mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu" 
          >
            <Menu size={24} />
          </button>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mobile-menu">
            <div className="flex mobile-menu-header">
              <Link to={`/${lang || i18n.language.split('-')[0]}`} onClick={() => setMobileMenuOpen(false)}>
                <img src="/logo.svg" alt="ShiftPic Logo" width={144} height={40} className="h-10" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="mobile-menu-close"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            <div className="mobile-menu-content">
              <NavItem
                to="/"
                active={activeLink === 'home'}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.converter')}
              </NavItem>
              <NavDropdown 
                active={activeLink === 'convert'} 
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              />
              <NavItem
                to="/articles"
                active={activeLink === 'articles'}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.articles')}
              </NavItem>
              <NavItem
                to="/plugins"
                active={activeLink === 'plugins'}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.plugins')}
              </NavItem>
              <NavItem
                to="/faq"
                active={activeLink === 'faq'}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.faq')}
              </NavItem>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;